import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export const PageHeader = ({ title, sub, actions }: { title: string; sub?: string; actions?: ReactNode }) => (
  <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
    <div>
      <h1 className="font-heading font-bold text-white text-xl sm:text-2xl">{title}</h1>
      {sub && <p className="text-gray-500 text-sm mt-1">{sub}</p>}
    </div>
    {actions && <div className="flex gap-2">{actions}</div>}
  </div>
);

export const StatCard = ({ label, value, accent = "#00E5FF", sub, to }: { label: string; value: ReactNode; accent?: string; sub?: string; to?: string }) => {
  const inner = (
    <>
      <p className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-gray-500">
        {label}
        {to && <ChevronRight className="h-3 w-3 text-gray-600 group-hover:text-[#A78BFA] transition-colors" />}
      </p>
      <p className="font-heading font-bold text-2xl mt-1" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={`statcard-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className="group block bg-[#121212] border border-white/10 rounded-lg p-4 hover:border-[#7C3AED]/50 hover:bg-[#16131f] transition-colors">
        {inner}
      </Link>
    );
  }
  return <div className="bg-[#121212] border border-white/10 rounded-lg p-4">{inner}</div>;
};

export const Table = ({ headers, children }: { headers: string[]; children: ReactNode }) => (
  <div className="bg-[#121212] border border-white/10 rounded-lg overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-white/10">
          {headers.map((h) => (
            <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest text-gray-500 px-4 py-3">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-white/5">{children}</tbody>
    </table>
  </div>
);

export const StatusBadge = ({ status }: { status: string }) => {
  const colors: Record<string, string> = {
    published: "text-green-400 bg-green-500/10 border-green-500/30",
    success: "text-green-400 bg-green-500/10 border-green-500/30",
    approved: "text-green-400 bg-green-500/10 border-green-500/30",
    sent: "text-green-400 bg-green-500/10 border-green-500/30",
    enforced: "text-green-400 bg-green-500/10 border-green-500/30",
    enacted: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    imported_pending_review: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30",
    open: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30",
    running: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
    in_progress: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
    proposed: "text-blue-400 bg-blue-500/10 border-blue-500/30",
    draft: "text-gray-400 bg-gray-500/10 border-gray-500/30",
    failed: "text-red-400 bg-red-500/10 border-red-500/30",
    rejected: "text-red-400 bg-red-500/10 border-red-500/30",
    hidden: "text-gray-400 bg-gray-500/10 border-gray-500/30",
  };
  return (
    <span className={`inline-block font-mono text-[10px] uppercase px-2 py-0.5 rounded border ${colors[status] || "text-gray-400 border-white/20"}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
};
